import {
  EditOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  message,
  Row,
  Select,
  Tooltip,
  Space,
  Table,
} from "antd";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import React, { useEffect, useState } from "react";
import API from "../../../api";
import CustomTable from "../../../components/Table/CustomTable";
import CustomerPaymentDrawer from "./PaymentCollectionDrawer";
import { getUserRole } from "../../../utils/auth";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { RangePicker } = DatePicker;
const { Option } = Select;

const CustomerPayment = () => {
  const [form] = Form.useForm();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [paymentsData, setPaymentsData] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCustomerData, setSelectedCustomerData] = useState(null);
  const [customers, setCustomers] = useState([]);
  // const [stores, setStores] = useState([]);
  const [stores, setStores] = useState([]); // State for stores
  const [customerData, setCustomerData] = useState({
    openingBalance: 0,
    currentBalance: 0,
    closingBalance: 0,
  });
  const [selectedDates, setSelectedDates] = useState([]);
  const [filters, setFilters] = useState({
    storeId: null,
    search: "",
    fromDate: null,
    toDate: null,
  });
  const [paginationInfo, setPaginationInfo] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [userRole, setUserRole] = useState(null);

  const handleDateChange = (dates) => {
    setSelectedDates(
      dates
        ? [dates[0]?.format("YYYY-MM-DD"), dates[1]?.format("YYYY-MM-DD")]
        : []
    );
  };

  const handleSearchChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value || null,
    }));
  };

  const handleFilter = () => {
    if (selectedDates.length === 2) {
      handleSearchChange("fromDate", selectedDates[0]);
      handleSearchChange("toDate", selectedDates[1]);
    } else {
      handleSearchChange("fromDate", null);
      handleSearchChange("toDate", null);
    }
  };

  const handleReset = () => {
    setSelectedDates([]);
    handleSearchChange("fromDate", null);
    handleSearchChange("toDate", null);
    setSelectedCustomer(null);
    // Allow all users to see all payments - no automatic store filtering
    handleSearchChange("storeId", null);
  };

  const formatFinanceName = (finance) => {
    if (!finance) return null;
    const words = finance
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .split(" ")
      .map(
        (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      );
    return words.join(" ");
  };

  const columns = [
    {
      title: "Sl. No",
      dataIndex: "slNo",
      key: "slNo",
      width: 80,
      fixed: "left",
      render: (text, record, index) =>
        (paginationInfo.current - 1) * paginationInfo.pageSize + index + 1,
    },
    {
      title: "Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      width: 100,
      render: (text) => {
        const date = new Date(text);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      },
    },
    !selectedCustomer && {
      title: "Customer Name",
      dataIndex: ["customerId", "name"],
      key: "customerName",
      width: 200,
      responsive: ["md"],
      render: (text, record) => {
        const address = record?.customerId?.address || {};
        return (
          <div>
            <div>{text}</div>
            <div style={{ fontSize: "12px", color: "#666" }}>
              {record.customerId?.phone || "N/A"} | {address?.city || "N/A"},{" "}
              {address?.street || "N/A"}
            </div>
          </div>
        );
      },
    },
    {
      title: "Branch Name",
      key: "branchName",
      width: 120,
      responsive: ["md"],
      render: (text, record) => {
        const storeId = record.saleId?.storeId;
        const store = stores.find((store) => store._id === storeId);
        return store?.name || "N/A";
      },
    },
    {
      title: "Inv No",
      dataIndex: "transactionId",
      key: "transactionId",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 80,
      render: (text) => text?.toUpperCase(),
    },
    {
      title: "Financer",
      dataIndex: ["saleId", "finance"],
      key: "finance",
      width: 120,
      responsive: ["lg"],
      render: (text) => formatFinanceName(text) || "--",
    },
    {
      title: "Payment Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      width: 120,
      responsive: ["md"],
      render: (text) => text?.toUpperCase(),
    },
    {
      title: "Inv Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      width: 120,
      render: (text) => `₹${text?.toLocaleString()}`,
    },
    {
      title: "Paid",
      dataIndex: "paid_amount",
      key: "paid_amount",
      width: 100,
      render: (text) => `₹${text?.toLocaleString()}`,
    },
    {
      title: "Balance (Adjustment)",
      dataIndex: "balance_amount",
      key: "balance_amount",
      width: 180,
      responsive: ["lg"],
      render: (text, record) => {
        const balance = text || 0;
        const adjustment = record.adjustment_amount || 0;
        // Calculate total paid for this transaction by summing paid_amount from related "pay" records
        const remaining = balance - record.balancePaid - adjustment;
        return (
          <div style={{ flexDirection: "column" }}>
            {remaining > 0 ? (
              <>
                <span style={{ color: remaining > 0 ? "red" : "green" }}>
                  {` ${
                    balance > 0
                      ? `₹ ${new Intl.NumberFormat().format(balance)}`
                      : 0
                  }  (₹ ${new Intl.NumberFormat().format(adjustment)})`}
                </span>
                {balance > 0 && (
                  <>
                    <br />
                    <span style={{ color: "black" }}>Paid: </span>
                    <span style={{ color: remaining > 0 ? "red" : "green" }}>
                      ₹{" "}
                      {new Intl.NumberFormat().format(
                        record.balancePaid + adjustment
                      )}
                    </span>
                    <br />
                    <span style={{ color: "black" }}>Remaining: </span>
                    <span style={{ color: remaining > 0 ? "red" : "green" }}>
                      ₹ {new Intl.NumberFormat().format(remaining)}
                    </span>
                  </>
                )}
              </>
            ) : (
              <span style={{ color: "green" }}>₹0</span>
            )}
          </div>
        );
      },
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 120,
      responsive: ["lg"],
      ellipsis: true,
      render: (text) => (
        <span style={{ color: text === "cancelled" ? "#ff4d4f" : "inherit" }}>
          {text || "N/A"}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (text, record) => {
        const balance = record.balance_amount || 0;
        const adjustment = record.adjustment_amount || 0;
        const relatedPayments = paymentsData.filter(
          (payment) =>
            payment.type === "pay" &&
            payment.transactionId === record.transactionId
        );
        const paid = relatedPayments.reduce(
          (sum, payment) => sum + (payment.paid_amount || 0),
          0
        );
        // const remaining = balance - adjustment - paid;
        const remaining = balance - adjustment - (record.balancePaid || 0);

        return (
          <>
            {record.type === "pay" && (
              <Button
                icon={<EditOutlined />}
                onClick={() => handleEditClick(record)}
                type="link"
              />
            )}
            {record.type === "inv" && (
              <Tooltip title="This Payment is not editable from here, Go to particular 'Sale' to Update this">
                <Button icon={<InfoCircleOutlined />} type="link" />
              </Tooltip>
            )}
            {remaining > 0 && customerData.closingBalance !== 0 && (
              <Button
                type="link"
                icon={<PlusOutlined />}
                onClick={() => handleMakePaymentClick(record)}
              />
            )}
          </>
        );
      },
    },
  ].filter(Boolean);

  const handleEditClick = (record) => {
    setSelectedPayment(record);
    setDrawerVisible(true);
  };

  const handleMakePaymentClick = (record) => {
    setSelectedPayment(null);
    setSelectedCustomerData({ ...record, selectedCustomer, customerData });
    setDrawerVisible(true);
  };

  const fetchCustomers = async () => {
    try {
      const response = await API.get("/customers", {
        params: {
          storeId: filters.storeId || undefined,
        },
      });
      setCustomers(response.data || []);
    } catch (error) {
      message.error("Failed to fetch customers.");
      console.error("Error fetching customers:", error);
    }
  };

  const fetchStores = async () => {
    try {
      const response = await API.get("/stores");
      setStores(response.data || []);
    } catch (error) {
      message.error("Failed to fetch stores.");
      console.error("Error fetching stores:", error);
    }
  };

  const fetchPayments = async () => {
    setLoading(true);
    try {
      let response;
      const { search, fromDate, toDate, storeId } = filters;

      if (selectedCustomer) {
        response = await API.get(`/payments/customer/${selectedCustomer}`, {
          params: {
            page: paginationInfo.current,
            limit: paginationInfo.pageSize,
            storeId: storeId || undefined,
            search,
            fromDate: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : null,
            toDate: toDate ? dayjs(toDate).format("YYYY-MM-DD") : null,
          },
        });
      } else {
        response = await API.get(`/payments/customer/all`, {
          params: {
            page: paginationInfo.current,
            limit: paginationInfo.pageSize,
            storeId: storeId || undefined,
            search,
            fromDate: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : null,
            toDate: toDate ? dayjs(toDate).format("YYYY-MM-DD") : null,
          },
        });
      }

      const { pagination, payments, totals } = response.data;

      // Show all payments regardless of store - no filtering by storeId
      const filteredPayments = payments;

      setPaymentsData(filteredPayments || []);

      // Compute totals on the frontend as a fallback
      const computedTotals = filteredPayments.reduce(
        (acc, payment) => {
          if (payment.type === "inv") {
            acc.totalAmount += payment.total_amount || 0;
            acc.balanceAmount +=
              payment.balance_amount - payment.balancePaid || 0;
            acc.paidAmount += payment.paid_amount + payment.balancePaid || 0;
          }
          return acc;
        },
        { totalAmount: 0, paidAmount: 0, balanceAmount: 0 }
      );

      // Use API totals if they match computed totals, otherwise use computed totals
      const apiTotals = {
        totalAmount: totals?.totalAmount || 0,
        paidAmount: totals?.paidAmount || 0,
        balanceAmount: totals?.balanceAmount || 0,
      };

      if (
        Math.abs(apiTotals.totalAmount - computedTotals.totalAmount) > 0.01 ||
        Math.abs(apiTotals.paidAmount - computedTotals.paidAmount) > 0.01 ||
        Math.abs(apiTotals.balanceAmount - computedTotals.balanceAmount) > 0.01
      ) {
        console.warn(
          "API totals do not match computed totals. Using computed totals.",
          { apiTotals, computedTotals }
        );
        setCustomerData({
          openingBalance: computedTotals.totalAmount,
          currentBalance: computedTotals.paidAmount,
          closingBalance: computedTotals.balanceAmount,
        });
      } else {
        setCustomerData({
          openingBalance: apiTotals.totalAmount,
          currentBalance: apiTotals.paidAmount,
          closingBalance: apiTotals.balanceAmount,
        });
      }

      setPaginationInfo((prev) => ({ ...prev, total: pagination?.total || 0 }));
    } catch (error) {
      message.error("Failed to fetch data.");
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const role = getUserRole();
    setUserRole(role);

    fetchStores().then((storeData) => {
      if (role.role !== "admin" && role.storeId) {
        handleSearchChange("storeId", role.storeId);
      }
    });

    fetchCustomers();
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [filters.storeId]);

  useEffect(() => {
    fetchPayments();
  }, [
    selectedCustomer,
    paginationInfo.current,
    paginationInfo.pageSize,
    filters.search,
    filters.fromDate,
    filters.toDate,
    filters.storeId,
  ]);

  return (
    <div
      style={{
        padding: "24px",
        background: "#f5f7fa",
        minHeight: "100vh",
        borderRadius: "12px",
      }}
    >
      {/* Header Section */}
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "5px",
          marginBottom: "10px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
          border: "1px solid #f0f0f0",
        }}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 0 }}>
          <Col xs={24} sm={12} md={6}>
            <h2
              style={{
                margin: 0,
                fontSize: "24px",
                fontWeight: "700",
                color: "#1a1a1a",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <span
                style={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  width: "4px",
                  height: "32px",
                  borderRadius: "2px",
                }}
              ></span>
              Customer Payments
            </h2>
          </Col>
          <Col xs={24} sm={24} md={24} lg={24}>
            <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
              <Col xs={24} sm={24} md={8} lg={8}>
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    padding: "16px 20px",
                    borderRadius: "12px",
                    color: "white",
                    textAlign: "center",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                    height: "100%",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      opacity: 0.9,
                      marginBottom: "8px",
                    }}
                  >
                    Total Amount
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "white",
                    }}
                  >
                    ₹{customerData.openingBalance.toLocaleString()}
                  </div>
                </div>
              </Col>

              <Col xs={24} sm={24} md={8} lg={8}>
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                    padding: "16px 20px",
                    borderRadius: "12px",
                    color: "white",
                    textAlign: "center",
                    boxShadow: "0 4px 12px rgba(240, 147, 251, 0.3)",
                    height: "100%",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      opacity: 0.9,
                      marginBottom: "8px",
                    }}
                  >
                    Paid Amount
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "white",
                    }}
                  >
                    ₹{customerData.currentBalance.toLocaleString()}
                  </div>
                </div>
              </Col>

              <Col xs={24} sm={24} md={8} lg={8}>
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                    padding: "16px 20px",
                    borderRadius: "12px",
                    color: "white",
                    textAlign: "center",
                    boxShadow: "0 4px 12px rgba(79, 172, 254, 0.3)",
                    height: "100%",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      opacity: 0.9,
                      marginBottom: "8px",
                    }}
                  >
                    Balance Amount
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "white",
                    }}
                  >
                    ₹{customerData.closingBalance.toLocaleString()}
                  </div>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>

      {/* Filters Section */}
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "16px",
          marginBottom: "16px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
          border: "1px solid #f0f0f0",
        }}
      >
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={24} md={12} lg={8}>
            <div
              style={{
                marginBottom: "8px",
                fontWeight: "500",
                color: "#595959",
              }}
            >
              Select Customer
            </div>
            <Select
              placeholder="Choose a customer to view payments"
              showSearch
              optionFilterProp="children"
              onChange={(value) => {
                setSelectedCustomer(value || null);
              }}
              filterOption={(input, option) =>
                option?.children?.toLowerCase()?.includes(input.toLowerCase())
              }
              value={selectedCustomer}
              style={{ width: "100%" }}
              allowClear
              size="large"
            >
              {customers.map((customer) => (
                <Option key={customer?._id} value={customer?._id}>
                  {customer?.name || "Unnamed Customer"}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={24} md={12} lg={6}>
            <div
              style={{
                marginBottom: "8px",
                fontWeight: "500",
                color: "#595959",
              }}
            >
              Date Range
            </div>
            <RangePicker
              value={
                selectedDates.length > 0
                  ? [dayjs(selectedDates[0]), dayjs(selectedDates[1])]
                  : []
              }
              onChange={handleDateChange}
              format="YYYY-MM-DD"
              style={{ width: "100%" }}
              size="large"
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={6}>
            <div
              style={{
                marginBottom: "8px",
                fontWeight: "500",
                color: "#595959",
              }}
            >
              Select Store
            </div>
            <Select
              showSearch
              placeholder="Choose store"
              value={filters.storeId}
              onChange={(value) => handleSearchChange("storeId", value)}
              style={{ width: "100%" }}
              allowClear
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              disabled={userRole?.role !== "admin"}
              size="large"
            >
              {stores.map((store) => (
                <Option key={store._id} value={store._id}>
                  {store.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={24} md={24} lg={4}>
            <div
              style={{
                marginBottom: "8px",
                fontWeight: "500",
                color: "#595959",
              }}
            >
              Actions
            </div>
            <Row gutter={[8, 8]}>
              <Col xs={12} sm={12} md={12} lg={12}>
                <Button
                  type="primary"
                  onClick={handleFilter}
                  size="large"
                  style={{ width: "100%" }}
                >
                  Filter
                </Button>
              </Col>
              <Col xs={12} sm={12} md={12} lg={12}>
                <Button
                  onClick={handleReset}
                  size="large"
                  style={{ width: "100%" }}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>

      {/* Transactions Card */}
      <Card
        title={
          <Row
            justify="space-between"
            align="middle"
            style={{ padding: "5px" }}
          >
            <Col>
              <h4
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1a1a1a",
                }}
              >
                Transactions
              </h4>
            </Col>
          </Row>
        }
        style={{
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          border: "1px solid #f0f0f0",
        }}
        bodyStyle={{ padding: "24px" }}
        bordered={false}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: "8px",
            overflow: "hidden",
            border: "1px solid #f0f0f0",
          }}
        >
          <CustomTable
            columns={columns}
            scroll={{ x: 1300 }}
            dataSource={paymentsData}
            onPaginationChange={(pagination) => {
              setPaginationInfo((prev) => ({
                ...prev,
                current: pagination.current,
                pageSize: pagination.pageSize,
              }));
            }}
            loading={loading}
            style={{
              borderRadius: "8px",
              overflow: "hidden",
            }}
            total={paginationInfo.total}
          />
        </div>
      </Card>

      <CustomerPaymentDrawer
        open={drawerVisible}
        setDrawerVisible={setDrawerVisible}
        handleSubmit={() => fetchPayments()}
        selectedPayment={selectedPayment}
        selectedCustomer={selectedCustomerData}
      />
    </div>
  );
};

export default CustomerPayment;
